/**
 * Slice 55 / T6–T16 — what wraps the real cleaner screens during a Test clean:
 * a slim "Test clean" bar with End test, the End test sheet, and the finished screen.
 */
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useInventoryItems } from "@/hooks/useInventory";
import { useMaintenanceReportsForTask } from "@/hooks/useMaintenanceReportsForTask";
import { collectSimplePhotoUrls, readSimpleFlowState } from "@/lib/simpleTurnover";
import { formatDurationWords, minutesBetween } from "@/lib/locationStamps";
import { itemHasLowWhen } from "@/lib/unitSetupChecklist";

export function TestCleanBar({ onEnd }: { onEnd: () => void }) {
  return (
    <div className="flex items-center gap-2.5 bg-[#0f1720] px-4 py-2 text-white">
      <span className="h-2 w-2 shrink-0 rounded-full bg-[#7fd3c8]" />
      <div className="text-[14px] font-bold">Test clean</div>
      <div className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-white/70">You’re the cleaner</div>
      <button
        type="button"
        onClick={onEnd}
        className="h-8 shrink-0 rounded-full border border-white/40 px-3.5 text-[13px] font-bold text-white"
      >
        End test
      </button>
    </div>
  );
}

export function TestCleanEndSheet({
  open,
  stepText,
  busy,
  onSave,
  onDiscard,
  onClose,
}: {
  open: boolean;
  stepText: string;
  busy: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[85] flex flex-col justify-end">
      <button type="button" className="absolute inset-0 bg-[rgba(15,23,32,0.55)]" aria-label="Close" onClick={() => !busy && onClose()} />
      <div className="relative z-10 flex flex-col gap-3 rounded-t-[26px] bg-white px-[18px] pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-5">
        <div className="mx-auto h-1 w-11 rounded-full bg-[#dde5eb]" />
        <div>
          <div className="text-[22px] font-bold tracking-[-0.4px]">End test clean?</div>
          <div className="mt-1 text-[15px] font-medium leading-snug text-[#475569]">
            You’re on {stepText}. Save keeps what you’ve done so far in Reports, marked Test clean · ended early.
          </div>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={onSave}
          className="flex h-14 items-center justify-center rounded-[15px] bg-[#24a89a] text-[17px] font-bold text-white"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "End and save"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDiscard}
          className="flex h-14 items-center justify-center rounded-[15px] border-[1.5px] border-[#efc6c0] bg-white text-[17px] font-bold text-[#b4453a]"
        >
          End and throw it away
        </button>
        <button type="button" disabled={busy} onClick={onClose} className="h-11 text-[16px] font-bold text-[#64748b]">
          Keep going
        </button>
      </div>
    </div>
  );
}

export function TestCleanFinished({
  propertyId,
  taskId,
  startedAt,
  completedAt,
  entryWalkthroughState,
  endedEarly,
}: {
  propertyId: string;
  taskId: string;
  startedAt: string | null;
  completedAt: string | null;
  entryWalkthroughState: unknown;
  endedEarly?: boolean;
}) {
  const navigate = useNavigate();
  const { data: reports = [] } = useMaintenanceReportsForTask(taskId);
  const { data: inventory = [] } = useInventoryItems(propertyId);

  const summary = useMemo(() => {
    const simple = readSimpleFlowState(entryWalkthroughState);
    const photos =
      collectSimplePhotoUrls(simple, "before").length +
      collectSimplePhotoUrls(simple, "after").length +
      (simple.keys_photo_url ? 1 : 0);
    const mins = startedAt && completedAt ? minutesBetween(startedAt, completedAt) : null;
    const low = (inventory as { quantity_type?: string | null; current_count?: number | null; threshold_count?: number | null; threshold_value?: number | null; current_value?: number | null }[]).filter(
      (it) => {
        if (!itemHasLowWhen(it)) return false;
        if ((it.quantity_type || "units").toLowerCase() === "fullness") {
          return it.current_value != null && it.threshold_value != null && it.current_value <= it.threshold_value;
        }
        return it.current_count != null && it.threshold_count != null && it.current_count <= it.threshold_count;
      },
    ).length;
    return [
      { k: "Time on job", v: mins != null ? formatDurationWords(mins) : "—", color: "#0f1720" },
      { k: "Photos", v: String(photos), color: "#0f1720" },
      { k: "Problems flagged", v: String(reports.filter((r) => r.status !== "resolved").length), color: reports.length ? "#b4453a" : "#15776d" },
      { k: "Short on stock", v: String(low), color: low ? "#8a5a00" : "#15776d" },
    ];
  }, [entryWalkthroughState, startedAt, completedAt, reports, inventory]);

  return (
    <div className="flex min-h-[70dvh] flex-col gap-6 bg-white px-6 pb-8 pt-10">
      <div className="flex h-[84px] w-[84px] items-center justify-center rounded-full bg-[#24a89a]">
        <Check className="h-10 w-10 text-white" strokeWidth={3} />
      </div>
      <div className="flex flex-col gap-2">
        <div className="text-[28px] font-bold leading-tight tracking-[-0.5px] text-[#0f1720]">
          {endedEarly ? "Test clean saved · ended early" : "Test clean finished"}
        </div>
        <div className="text-[15.5px] font-medium leading-snug text-[#475569]">
          It’s in Reports under your name, marked Test clean. It never counts in stats or billing.
        </div>
      </div>
      <div className="flex flex-col border-t-[1.5px] border-[#e6edf1]">
        {summary.map((s) => (
          <div key={s.k} className="flex h-14 items-center justify-between border-b-[1.5px] border-[#e6edf1]">
            <div className="text-[16px] font-medium text-[#64748b]">{s.k}</div>
            <div className="text-[17px] font-bold" style={{ color: s.color }}>
              {s.v}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-auto flex flex-col gap-2.5">
        <button
          type="button"
          onClick={() => navigate(`/properties/${propertyId}?tab=reports`)}
          className={cn("flex h-[58px] items-center justify-center rounded-2xl bg-[#24a89a] text-[17.5px] font-bold text-white")}
        >
          See the report
        </button>
        <button
          type="button"
          onClick={() => navigate(`/properties/${propertyId}`)}
          className="flex h-[54px] items-center justify-center rounded-2xl border-[1.5px] border-[#d7e0e6] text-[16.5px] font-bold text-[#0f1720]"
        >
          Back to property
        </button>
      </div>
    </div>
  );
}
